import type { AppUser } from '@/contexts/AuthContext'
import { hasPermission, type AppPermission } from '@/lib/auth/permissions'

type FirestoreReadOperation =
  | 'fetchDocument'
  | 'listenDocument'
  | 'fetchCollection'
  | 'listenCollection'

type FirestoreQueryDiagnostic = {
  agencyScoped?: boolean
  orderByField?: string | null
  orderDirection?: 'asc' | 'desc'
  limitCount?: number
  additionalConstraintCount?: number
}

export type FirestorePermissionDiagnostic = {
  type: 'firestore-permission-denied'
  collectionName: string
  documentPath?: string
  operation: FirestoreReadOperation
  code?: string
  expectedPermission?: AppPermission
  user: {
    uid?: string
    agencyId?: string
    role?: string
    primaryRole?: string
    status?: string
    permissionCount: number
    hasExpectedPermission?: boolean
  }
  query?: FirestoreQueryDiagnostic
  likelyCause: string
  nextStep: string
}

type FirestoreDiagnosticParams = {
  collectionName: string
  documentPath?: string
  operation: FirestoreReadOperation
  user?: AppUser | null
  expectedPermission?: AppPermission
  query?: FirestoreQueryDiagnostic
}

const READ_PERMISSION_BY_COLLECTION: Partial<Record<string, AppPermission>> = {
  appeals: 'billing.read',
  auditLogs: 'audit.read',
  authorizations: 'authorizations.read',
  claims: 'billing.read',
  eligibilityChecks: 'billing.read',
  eraPayments: 'billing.read',
  evvRecords: 'evv.read',
  evvExceptions: 'evv_exceptions.read',
  familyFeedback: 'family_feedback.read',
  notifications: 'notifications.read',
  payerEnrollments: 'billing.read',
  plansOfCare: 'forms.read',
}

export class FirestorePermissionError extends Error {
  code?: string
  diagnostic: FirestorePermissionDiagnostic
  originalError: unknown

  constructor(originalError: unknown, diagnostic: FirestorePermissionDiagnostic) {
    super(getErrorMessage(originalError))
    this.name = 'FirestorePermissionError'
    this.code = diagnostic.code
    this.diagnostic = diagnostic
    this.originalError = originalError
  }
}

export function getExpectedReadPermission(collectionName: string): AppPermission | undefined {
  const rootCollection = collectionName.split('/')[0]
  return READ_PERMISSION_BY_COLLECTION[rootCollection]
}

export function isFirestorePermissionDenied(error: unknown): boolean {
  return getErrorCode(error) === 'permission-denied'
}

export function isFirestorePermissionError(error: unknown): error is FirestorePermissionError {
  return error instanceof FirestorePermissionError
}

export function normalizeFirestoreReadError(
  error: unknown,
  params: FirestoreDiagnosticParams
): Error {
  if (!isFirestorePermissionDenied(error)) {
    return error instanceof Error ? error : new Error(getErrorMessage(error))
  }

  const expectedPermission = params.expectedPermission ?? getExpectedReadPermission(params.collectionName)
  const hasExpectedPermission = expectedPermission && params.user
    ? hasPermission(params.user, expectedPermission)
    : undefined

  return new FirestorePermissionError(error, {
    type: 'firestore-permission-denied',
    collectionName: params.collectionName,
    documentPath: params.documentPath,
    operation: params.operation,
    code: getErrorCode(error),
    expectedPermission,
    user: {
      uid: params.user?.uid,
      agencyId: params.user?.agencyId,
      role: params.user?.role,
      primaryRole: params.user?.primaryRole,
      status: params.user?.status,
      permissionCount: params.user?.permissions?.length ?? 0,
      hasExpectedPermission,
    },
    query: params.query,
    likelyCause: getLikelyCause(params.user, expectedPermission, hasExpectedPermission),
    nextStep: getNextStep(params.collectionName, expectedPermission, hasExpectedPermission),
  })
}

export function logFirestoreReadError(message: string, error: Error): void {
  if (isFirestorePermissionError(error)) {
    console.error(message, error.originalError, {
      diagnostic: error.diagnostic,
    })
    return
  }

  console.error(message, error)
}

export function getFirestoreErrorDescription(error: Error): string {
  if (!isFirestorePermissionError(error)) {
    return error.message || 'An unexpected Firestore error occurred.'
  }

  const { diagnostic } = error
  const permissionText = diagnostic.expectedPermission
    ? ` Expected permission: ${diagnostic.expectedPermission}.`
    : ''
  const roleText = diagnostic.user.role
    ? ` Current role: ${diagnostic.user.role}.`
    : ''

  return `${diagnostic.likelyCause}${permissionText}${roleText}`
}

function getLikelyCause(
  user: AppUser | null | undefined,
  expectedPermission: AppPermission | undefined,
  hasExpectedPermission: boolean | undefined
): string {
  if (!user) {
    return 'The signed-in user context was not available when Firestore denied the read.'
  }

  if (!user.agencyId) {
    return 'The signed-in user is missing an agency ID, so agency-scoped Firestore rules denied the read.'
  }

  if (expectedPermission && hasExpectedPermission === false) {
    return 'The signed-in user does not have the client-side permission expected for this data.'
  }

  return 'Firestore rules denied the read even though the app attempted an agency-scoped request.'
}

function getNextStep(
  collectionName: string,
  expectedPermission: AppPermission | undefined,
  hasExpectedPermission: boolean | undefined
): string {
  if (expectedPermission && hasExpectedPermission === false) {
    return `Review the user's role or explicit permissions for ${expectedPermission}.`
  }

  return `Compare the ${collectionName} rule with the user's auth claims and /users/{uid} document.`
}

function getErrorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code
    return typeof code === 'string' ? code : undefined
  }

  return undefined
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'string') return error
  return 'Unknown Firestore error'
}
